import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Flex,
    IconChecklistOutline,
    IconDollarSignCircleOutline,
    IconYardSignOutline,
    Image,
    Spacer,
    Text,
} from '@zillow/constellation';

import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';

// actions
import PostingActions from 'app/shared/actions/PostingActions';

// selectors
import { PropertiesSelectors, RoutingSelectors } from 'app/shared/selectors';

// assets
import zBluePersonCircle from 'app/shared/images/z-blue-person-circle.svg';

import { StyledModalSection } from 'app/shared/containers/ModalContainer/StyledComponents';

import {
    StyledCenterText,
    StyledDivider,
    StyledFeatureList,
    StyledIcon,
    StyledListItem,
} from './StyledComponents';

const defaultPrice = '$9.99';

const LeadGuarantee = () => {
    // selectors
    const currentProperty = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));
    const listingId = useSelector(state => RoutingSelectors.makeListingId(state));

    // required vars before hooks
    const { leadGuarantee, paidInclusion } = currentProperty;
    const { price: paidInclusionPrice } = paidInclusion;

    // hooks
    const dispatch = useDispatch();
    const [price, setPrice] = useState(defaultPrice);

    useEffect(() => {
        // check price from redux store
        if (paidInclusionPrice === '') {
            // no price set - fetch price
            dispatch(PostingActions.property.getDetails(listingId))
                .catch(() => {
                    // error when fetching - set price to default price
                    setPrice(defaultPrice);
                });
        } else {
            // use value from redux store
            setPrice(paidInclusionPrice);
        }
    }, [paidInclusionPrice, listingId]);

    const { isEligible: isEligibleForLeadGuarantee } = leadGuarantee;

    const renderPriceItemContent = () => (
        <StyledListItem>
            <StyledIcon>
                <IconDollarSignCircleOutline />
            </StyledIcon>
            <Text type="body">
                Post your&nbsp;
                <b>first listing for free</b>
                ,&nbsp;and pay only&nbsp;
                <b>{`${price}/week`}</b>
                &nbsp;for additional listings.
            </Text>
        </StyledListItem>
    );

    const renderLeadGuaranteeBanner = () => {
        const heading = 'List with confidence.';
        const body = 'If you don\'t receive a lead within a week, it\'s free to list until you do (for up to 8 weeks).';
        const actionLabel = 'Learn more about our Lead Guarantee';
        const actionPath = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360048462014';

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                actionLabel={actionLabel}
                actionPath={actionPath}
                actionTarget="_blank"
                iconWidthPX="60px"
                paddingXLOnDesktop={true}
            />
        );
    };

    return (
        <div className="LeadGuarantee">
            <Spacer marginBottom="sm">
                <StyledModalSection>
                    <Spacer className="PIVPM-ImageContainer" marginBottom="md">
                        <Flex
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                        >
                            <Image src={zBluePersonCircle} alt="background" />
                        </Flex>
                    </Spacer>

                    <Spacer className="PIVPM-content">
                        <Spacer marginBottom="xs">
                            <StyledCenterText as="div" fontType="h6">
                            The hassle-free way to find renters fast
                            </StyledCenterText>
                        </Spacer>

                        <StyledCenterText as="div" fontType="body">
                    Fill your vacancies, screen renters and more with Zillow Rental Manager
                        </StyledCenterText>
                    </Spacer>

                    <Spacer className="PIVPM-divider" marginY="md">
                        <Flex
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                        >
                            <StyledDivider />
                        </Flex>
                    </Spacer>

                    <Spacer className="PIVPM-ListContainer" marginBottom="xl">
                        <StyledFeatureList>
                            <StyledListItem>
                                <StyledIcon>
                                    <IconYardSignOutline />
                                </StyledIcon>
                                <Text type="body">
                            List once and reach renters on Zillow, Trulia and HotPads.
                                </Text>
                            </StyledListItem>

                            <StyledListItem>
                                <StyledIcon>
                                    <IconChecklistOutline />
                                </StyledIcon>
                                <Text type="body">
                            Screen renters online with criminal background checks and credit reports.
                                </Text>
                            </StyledListItem>

                            {renderPriceItemContent()}
                        </StyledFeatureList>
                    </Spacer>
                </StyledModalSection>
            </Spacer>

            {isEligibleForLeadGuarantee && (
                <Spacer className="PIVPM-BannerContainer">
                    {renderLeadGuaranteeBanner()}
                </Spacer>
            )}
        </div>
    );
};

LeadGuarantee.propTypes = {};

LeadGuarantee.defaultProps = {};

export default LeadGuarantee;
